import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchServices } from '../../api/services';
import ServiceCard from './ServiceCard';
import ServiceDetails from './service-details/ServiceDetails';
import RightSidebar from '../layout/RightSidebar';

import styles from './styles/ServicesList.module.css';

const gridVariants = {
    hidden: {},
    show: {
        transition: {
            staggerChildren: 0.06,
            delayChildren: 0.08,
        },
    },
};

const ServicesList = ({ type = 'all' }) => {
    const [services, setServices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [selectedServiceId, setSelectedServiceId] = useState(null);
    const observer = useRef();

    const location = useLocation();
    const navigate = useNavigate();

    const selectedService = useMemo(() => 
        services?.find(service => service.id === selectedServiceId) || null,
        [services, selectedServiceId]
    );

    const sortedServices = useMemo(() => {
        return [...services].sort((a, b) => {
            if (a.isDefault === b.isDefault) return 0;
            return a.isDefault ? -1 : 1;
        });
    }, [services]);

    const lastServiceRef = useCallback(node => {
        if (isLoading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        });

        if (node) observer.current.observe(node);
    }, [isLoading, hasMore]);

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await fetchServices(type, page);
            setServices(prev => page === 1 ? data.services : [...prev, ...data.services]);
            setHasMore(data.hasMore);
        } catch (err) {
            setError('Failed to load services');
            console.error('Error fetching services:', err);
        } finally {
            setIsLoading(false);
        }
    }, [page, type]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Open service details if serviceId is provided in query string
    useEffect(() => {
        if (!services || services.length === 0) return;
        const params = new URLSearchParams(location.search);
        const serviceIdParam = params.get('serviceId');
        if (serviceIdParam) {
            const exists = services.find(s => s.id === serviceIdParam);
            if (exists) {
                setSelectedServiceId(serviceIdParam);
                // Remove serviceId from URL after opening details
                params.delete('serviceId');
                const newSearch = params.toString();
                navigate({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' }, { replace: true });
            }
        }
    }, [services, location.search, location.pathname, navigate]);

    const handleServiceClick = useCallback(service => {
        setSelectedServiceId(service.id);
    }, []);

    const handleCloseDetails = useCallback(() => {
        setSelectedServiceId(null);
    }, []);

    if (error) {
        return <div className={styles.error}>{error}</div>;
    }

    return (
        <>
            <div className={styles.container}>
                {sortedServices.length > 0 && (
                    <motion.div
                        className={styles.grid}
                        variants={gridVariants}
                        initial="hidden"
                        animate="show"
                    >
                        <AnimatePresence mode="popLayout">
                            {sortedServices.map((service, index) => (
                                <ServiceCard
                                    key={service.id}
                                    service={service}
                                    setServices={setServices}
                                    onClick={() => handleServiceClick(service)}
                                    type={type}
                                    ref={index === sortedServices.length - 1 ? lastServiceRef : null}
                                    isShowDelete={type !== 'owned'}
                                />
                            ))}
                        </AnimatePresence>
                    </motion.div>
                )}
                {isLoading && (
                    <div className={styles.text}>Loading services...</div>
                )}
                {services.length === 0 && !isLoading && (
                    <div className={styles.text}>No services found</div>
                )}
            </div>

            <RightSidebar
                isOpen={!!selectedServiceId}
                onClose={handleCloseDetails}
            >
                {selectedService && (
                    <ServiceDetails
                        service={selectedService}
                        isAdded={selectedService?.isAdded}
                        isEnabled={selectedService?.isEnabled}
                        setServices={setServices}
                    />
                )}
            </RightSidebar>
        </>
    );
};

export default memo(ServicesList);
